// Pure logic for the evaluation process (stages, gates, readiness, points and
// slot forms). No DB state and no I/O: the backend enforces with these and the
// frontend renders with the same functions, so the two cannot disagree.
// Types and constants: evaluation-process.types.ts. Design:
// docs/specs/2026-10-13-evaluation-process-design.md.
import { EVAL_GATES, EVAL_GATES_NEEDING_NO_OPEN_POINTS, EVAL_PROCESS_COMPLETE, EVAL_PROCESS_ERROR_CODES, EVAL_PROCESS_SLOTS, EVAL_PROCESS_STAGE_NAMES, EVAL_PROCESS_STAGES, EVAL_SAMPLE_STAGE_FIELD_PATH, EVAL_ENGINEER_FIELD_PATH, EVAL_FIRMWARE_VERSION_FIELD_PATH, EVAL_PER_VERSION_SLOTS, EVAL_SOFTWARE_VERSION_MAX, EVAL_SOFTWARE_VERSION_SLOT, EVAL_SOFTWARE_VERSION_STAGE, EVAL_STAGE_OPTIONAL_SLOTS, EVAL_SLOT_DONE_STATUSES, EVAL_SLOT_FILE_CONTENT_TYPES, EVAL_SLOT_FORMS, EVAL_SLOT_KIND, EVAL_SLOT_UNPREVIEWABLE_PHOTO_EXTENSIONS, EVAL_SLOT_UPLOADS, EVAL_STAGE_SAMPLE_TYPE, EVAL_STAGE_SLOTS, EVAL_UX_HEADINGS, EVAL_UX_HEADING_INFO, GATE_AFTER_STAGE, STAGE_BEFORE_GATE, STAGE_SAMPLE_ANSWER_PREFERENCES, } from './evaluation-process.types.js';
import { canReleaseEvalGate } from './user.types.js';
// ─── Stages and gates ───────────────────────────────────────────────────────
/** A stored value that is one of the three stages (a submission's `item.evaluationStage`, say). */
export function isEvalProcessStage(value) {
    return EVAL_PROCESS_STAGES.includes(value);
}
/** A stored value that is one of the slots. */
export function isEvalProcessSlot(value) {
    return EVAL_PROCESS_SLOTS.includes(value);
}
/**
 * How many gates are released, counting only the in-order prefix A, B, C. A
 * release log that skipped a gate (which the server never writes) cannot move
 * the product past the gap.
 */
function releasedGateCount(gates) {
    const released = new Set(gates.map((g) => g.gate));
    let count = 0;
    while (count < EVAL_GATES.length && released.has(EVAL_GATES[count]))
        count++;
    return count;
}
/** The product's current stage, derived from its gate releases: none → cnc, A → iteration, A+B → pls, all → complete. */
export function currentStage(gates) {
    const count = releasedGateCount(gates);
    return count >= EVAL_PROCESS_STAGES.length ? EVAL_PROCESS_COMPLETE : EVAL_PROCESS_STAGES[count];
}
/** The gate to release next, or null once all three are released. */
export function nextGate(gates) {
    const count = releasedGateCount(gates);
    return count >= EVAL_GATES.length ? null : EVAL_GATES[count];
}
// ─── Which samples a stage evaluates (D16) ───────────────────────────────────
/** The stages a sample of this type is evaluated at, in order: CNC → 1 and 2, PLS → 3. */
export function evalStagesForSampleType(sampleType) {
    return EVAL_PROCESS_STAGES.filter((stage) => EVAL_STAGE_SAMPLE_TYPE[stage] === sampleType);
}
/**
 * Whether a sample of this type may be worked at this stage (D16): CNC samples
 * at stages 1 and 2, PLS samples at stage 3, either site. A sample with no type
 * cannot be worked until it is labelled. Once the process is complete there is
 * no stage to work, so nothing is refused here (a review session is allowed;
 * work is refused by EVAL_PROCESS_COMPLETE instead).
 */
export function checkEvalSampleStage(sampleType, stage) {
    if (stage === EVAL_PROCESS_COMPLETE)
        return { ok: true };
    if (!sampleType)
        return { ok: false, reason: 'unlabelled' };
    const required = EVAL_STAGE_SAMPLE_TYPE[stage];
    if (required === sampleType)
        return { ok: true };
    return { ok: false, reason: 'mismatch', sampleType, required, stagesForSample: evalStagesForSampleType(sampleType) };
}
/** 1, 2 or 3: the number the stage is shown with ("1 CNC sample"). */
export function evalStageNumber(stage) {
    return EVAL_PROCESS_STAGES.indexOf(stage) + 1;
}
/** "1 CNC sample", "2 Back-and-forth", "3 PLS sample". */
export function evalStageLabel(stage) {
    return `${evalStageNumber(stage)} ${EVAL_PROCESS_STAGE_NAMES[stage]}`;
}
/** Where a stage sits relative to the product's current one, for the board. */
export function evalStagePosition(stage, current) {
    if (current === EVAL_PROCESS_COMPLETE)
        return 'past';
    const diff = EVAL_PROCESS_STAGES.indexOf(stage) - EVAL_PROCESS_STAGES.indexOf(current);
    if (diff < 0)
        return 'past';
    return diff === 0 ? 'current' : 'future';
}
// ─── Slots and forms ────────────────────────────────────────────────────────
/** How the slot is worked: a form, upload files then Completed, or points then Completed. */
export function evalSlotKind(slot) {
    return EVAL_SLOT_KIND[slot];
}
/** True when the slot opens a form. */
export function evalSlotHasForm(slot) {
    return EVAL_SLOT_KIND[slot] === 'form' && EVAL_SLOT_FORMS[slot] !== null;
}
/** True when the slot is worked by uploading files and pressing Completed. */
export function evalSlotIsUpload(slot) {
    return EVAL_SLOT_KIND[slot] === 'upload_complete' && EVAL_SLOT_UPLOADS[slot] !== null;
}
/** True when the slot is worked by raising points under headings and pressing Completed. */
export function evalSlotIsPoints(slot) {
    return EVAL_SLOT_KIND[slot] === 'points_complete';
}
/** True when the slot is worked by cross-checking the factory's figures and pressing Completed (PM Spec Review, D28). */
export function evalSlotIsReview(slot) {
    return EVAL_SLOT_KIND[slot] === 'review_complete';
}
/** True when the slot is done by pressing Completed (upload, points and review slots), not by a finished evaluation. */
export function evalSlotIsCompletable(slot) {
    return evalSlotIsUpload(slot) || evalSlotIsPoints(slot) || evalSlotIsReview(slot);
}
/** True when a stage lists the slot (Photos is at CNC and PLS only). */
export function isEvalSlotInStage(stage, slot) {
    return EVAL_STAGE_SLOTS[stage].includes(slot);
}
/** The slots that open a form, in display order. */
export const EVAL_FORM_SLOTS = EVAL_PROCESS_SLOTS.filter(evalSlotHasForm);
/** The upload + Completed slots, in display order. */
export const EVAL_UPLOAD_SLOTS = EVAL_PROCESS_SLOTS.filter(evalSlotIsUpload);
/** The slots done by Completed (upload and points slots), in display order. */
export const EVAL_COMPLETABLE_SLOTS = EVAL_PROCESS_SLOTS.filter(evalSlotIsCompletable);
/**
 * The slots the gate closing `stage` waits on (D3), in display order: every
 * slot the stage lists that is a form, an upload or a points slot, except an
 * optional one (Dim Curve at Back-and-forth, D37).
 */
export function evalReadinessSlots(stage) {
    return EVAL_STAGE_SLOTS[stage].filter((slot) => (evalSlotHasForm(slot) || evalSlotIsCompletable(slot)) && !evalSlotIsOptionalAt(stage, slot));
}
/** True for a slot a stage lists that its gate does not wait on (Dim Curve at Back-and-forth, D37). */
export function evalSlotIsOptionalAt(stage, slot) {
    return (EVAL_STAGE_OPTIONAL_SLOTS[stage] ?? []).includes(slot);
}
/**
 * The stages that open with their "Points to resolve" (D33, extended to PLS
 * on 2026-10-14): those whose closing gate needs zero Open points (B and C,
 * `EVAL_GATES_NEEDING_NO_OPEN_POINTS`), so Back-and-forth and PLS.
 */
export function evalStageResolvesPoints(stage) {
    return EVAL_GATES_NEEDING_NO_OPEN_POINTS.includes(GATE_AFTER_STAGE[stage]);
}
/** The gate whose release opened `stage`; null for the first stage. */
export function evalGateOpening(stage) {
    const index = EVAL_PROCESS_STAGES.indexOf(stage);
    return index > 0 ? GATE_AFTER_STAGE[EVAL_PROCESS_STAGES[index - 1]] : null;
}
// ─── Back-and-forth: software versions (D33) ────────────────────────────────
/** True for Software Base Eval at Back-and-forth: its row is the software versions (D33, D38). */
export function evalSlotIsSoftwareVersions(stage, slot) {
    return stage === EVAL_SOFTWARE_VERSION_STAGE && slot === EVAL_SOFTWARE_VERSION_SLOT;
}
/** True for an upload slot kept per software version at `stage` (Dim Curve at Back-and-forth). */
export function evalSlotIsPerVersion(stage, slot) {
    return stage === EVAL_SOFTWARE_VERSION_STAGE && EVAL_PER_VERSION_SLOTS.includes(slot);
}
/**
 * A version string as stored: trimmed, inner runs of white space made one
 * space. Null when blank or longer than {@link EVAL_SOFTWARE_VERSION_MAX}.
 */
export function normaliseEvalSoftwareVersion(value) {
    if (typeof value !== 'string')
        return null;
    const label = value.trim().replace(/\s+/g, ' ');
    return label.length > 0 && label.length <= EVAL_SOFTWARE_VERSION_MAX ? label : null;
}
/** Two version strings name the same version: compared without case or surrounding space. */
export function sameEvalSoftwareVersion(a, b) {
    const x = normaliseEvalSoftwareVersion(a);
    const y = normaliseEvalSoftwareVersion(b);
    return x !== null && y !== null && x.toLowerCase() === y.toLowerCase();
}
/**
 * The newest of a product's software versions (D33, D38): latest created,
 * the higher key on a tie. Null for none. `createdAt` is ISO.
 */
export function latestEvalSoftwareVersion(versions) {
    let latest = null;
    for (const version of versions) {
        if (!latest ||
            version.createdAt > latest.createdAt ||
            (version.createdAt === latest.createdAt && version.versionKey > latest.versionKey)) {
            latest = version;
        }
    }
    return latest;
}
// ─── Back-and-forth: software version reviews (D38) ─────────────────────────
/**
 * A software point (D38): its Software tick when one is set; else a point of
 * the software form (Software Base Eval: its questions' Fail points, and
 * points raised on it).
 */
export function evalPointIsSoftware(point) {
    if (typeof point.software === 'boolean')
        return point.software;
    return point.evaluationSlot === EVAL_SOFTWARE_VERSION_SLOT;
}
/** The point's newest outcome on one version, or null. */
export function latestEvalVersionCheck(checks, versionKey) {
    let latest = null;
    for (const check of checks ?? []) {
        if (check.versionKey !== versionKey)
            continue;
        if (!latest || new Date(check.at).getTime() >= new Date(latest.at).getTime())
            latest = check;
    }
    return latest;
}
/**
 * The point's software history (D38): its newest outcome on each version, in
 * the order the versions were first checked. "V1.0.7 still open → V1.0.9 fixed".
 */
export function evalPointVersionTrail(checks) {
    const order = [];
    for (const check of checks ?? [])
        if (!order.includes(check.versionKey))
            order.push(check.versionKey);
    return order.map((key) => latestEvalVersionCheck(checks, key));
}
const timeOf = (value) => new Date(value).getTime();
/**
 * One version's review (D38): the points it lists, in order, and how many
 * are reviewed. Pass every point of the product (deleted ones are left out
 * here) in the list's order, and when the next version was added (null for
 * the latest).
 *
 * - Listed: the Open software points when it was added (`pointIds`); then,
 *   as "Added in this version", the software points raised in its review or
 *   checked on it, those raised in its time (before the next version), and,
 *   on the latest version, every software point Open now.
 * - Reviewed: no longer Open (Closed, Future: settled however it was), or
 *   its newest outcome on this version is "still open" or "raised". A point
 *   marked fixed on it and reopened since needs a look again.
 * - Done when every listed point is reviewed (none listed: done).
 */
export function evalSoftwareReview(version, points, nextCreatedAt) {
    const from = timeOf(version.createdAt);
    const to = nextCreatedAt === null ? Number.POSITIVE_INFINITY : timeOf(nextCreatedAt);
    const live = points.filter((p) => !p.deletedAt);
    const checkedHere = (p) => latestEvalVersionCheck(p.versionChecks, version.versionKey);
    let snapshot;
    if (version.pointIds) {
        const byId = new Map(live.map((p) => [p.pointId, p]));
        snapshot = version.pointIds.map((id) => byId.get(id)).filter((p) => !!p);
    }
    else {
        snapshot = live.filter((p) => evalPointIsSoftware(p) &&
            timeOf(p.createdAt) <= from &&
            (isEvalPointOpen(p) || (!!p.statusChange && timeOf(p.statusChange.at) >= from) || !!checkedHere(p)));
    }
    const listed = new Set(snapshot.map((p) => p.pointId));
    const added = live.filter((p) => {
        if (listed.has(p.pointId))
            return false;
        if (checkedHere(p))
            return true;
        if (!evalPointIsSoftware(p))
            return false;
        const raised = timeOf(p.createdAt);
        if (raised >= from && raised < to)
            return true;
        return nextCreatedAt === null && isEvalPointOpen(p);
    });
    const items = [
        ...snapshot.map((p) => ({ point: p, added: false })),
        ...added.map((p) => ({ point: p, added: true })),
    ].map(({ point, added: isAdded }) => {
        const outcome = checkedHere(point)?.outcome ?? null;
        const reviewed = !isEvalPointOpen(point) || outcome === 'still_open' || outcome === 'raised';
        return { pointId: point.pointId, added: isAdded, reviewed };
    });
    const reviewed = items.filter((i) => i.reviewed).length;
    return { items, reviewed, total: items.length, done: reviewed === items.length };
}
/** A stored value that is one of the PM Product Evaluation headings. */
export function isEvalUxHeading(value) {
    return EVAL_UX_HEADINGS.includes(value);
}
/** True for III, V and VI: the product-type headings that can be switched to Not applicable. */
export function evalUxHeadingCanBeNotApplicable(heading) {
    return EVAL_UX_HEADING_INFO[heading].canBeNotApplicable;
}
/** The file's extension, lower case with its dot ('.ies'), or '' when it has none. */
export function evalSlotFileExtension(filename) {
    const name = filename.trim();
    const dot = name.lastIndexOf('.');
    return dot > 0 && dot < name.length - 1 ? name.slice(dot).toLowerCase() : '';
}
/** True when an upload slot accepts a file of this name (by extension). Always false for any other slot. */
export function isEvalSlotFileAccepted(slot, filename) {
    const accept = EVAL_SLOT_UPLOADS[slot]?.accept ?? [];
    const ext = evalSlotFileExtension(filename);
    return ext !== '' && accept.includes(ext);
}
/** True for a photo the browser can draw as a thumbnail; false for HEIC/HEIF and anything not an image. */
export function isEvalSlotPhotoPreviewable(filename) {
    const ext = evalSlotFileExtension(filename);
    return ((EVAL_SLOT_UPLOADS.photos?.accept ?? []).includes(ext) &&
        !EVAL_SLOT_UNPREVIEWABLE_PHOTO_EXTENSIONS.includes(ext));
}
/** The content type a slot file is stored with, by extension; octet-stream when unknown. */
export function evalSlotFileContentType(filename) {
    return EVAL_SLOT_FILE_CONTENT_TYPES[evalSlotFileExtension(filename)] ?? 'application/octet-stream';
}
/** A stage × slot grid with every cell from `fill`. */
export function evalStageSlotGrid(fill) {
    const grid = {};
    for (const stage of EVAL_PROCESS_STAGES) {
        const row = {};
        for (const slot of EVAL_PROCESS_SLOTS)
            row[slot] = fill(stage, slot);
        grid[stage] = row;
    }
    return grid;
}
/** One stage × slot cell of a sparse map, or null. Tolerates a stored map with an off-grid key. */
export function evalStageSlotCell(map, stage, slot) {
    return map?.[stage]?.[slot] ?? null;
}
/**
 * The section a submission form keeps out of `enabledSections` entirely. Both
 * DraftEvaluationForm and the live EvaluationContext build their default map
 * from every section except this one.
 */
const META_SECTION_ID = 'meta';
/**
 * The `enabledSections` map a slot's new submission is created with, built
 * from the LIVE definition's section ids (D4). The live form renders a section
 * only when its key is true, so every live section gets a key.
 *
 * A form with `sectionIds: null` gets the full form, as "New Eval" does today.
 * A limited form whose listed ids are not ALL live falls back to the full form
 * rather than opening a form with a section silently missing.
 */
export function evalSlotEnabledSections(form, liveSectionIds) {
    const live = liveSectionIds.filter((id) => id !== META_SECTION_ID);
    const liveSet = new Set(live);
    const missingSectionIds = (form.sectionIds ?? []).filter((id) => !liveSet.has(id));
    const limited = form.sectionIds !== null && missingSectionIds.length === 0;
    const keep = new Set(limited && form.sectionIds ? form.sectionIds : live);
    // The identity section is always on, whatever the slot lists.
    if (form.identitySectionId && liveSet.has(form.identitySectionId))
        keep.add(form.identitySectionId);
    const enabledSections = {};
    for (const id of live)
        enabledSections[id] = keep.has(id);
    return {
        enabledSections,
        limited,
        missingSectionIds,
        focusSectionId: form.focusSectionId && liveSet.has(form.focusSectionId) ? form.focusSectionId : null,
    };
}
/**
 * The sample-stage answer for a stage, picked from the live select's options
 * by `STAGE_SAMPLE_ANSWER_PREFERENCES` (the closest option the form has), or
 * the first preference when the options are unknown. Null when the form
 * offers none of them: nothing is prefilled rather than a value the select
 * cannot show.
 */
export function evalSampleStageAnswer(stage, options) {
    const preferences = STAGE_SAMPLE_ANSWER_PREFERENCES[stage];
    if (!options)
        return preferences[0] ?? null;
    return preferences.find((answer) => options.includes(answer)) ?? null;
}
/**
 * True when a stored answer is blank: absent, null, an empty or whitespace
 * string, or an empty list, whether bare or inside a `FieldValue`. The
 * autofill only ever writes a blank answer.
 */
export function isBlankEvalAnswer(entry) {
    const value = answerValue(entry);
    if (value === undefined || value === null)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
/**
 * The identity section of the form a process submission was created from
 * (its template product id), or null: what `EvaluationForm` locks on in the
 * Enable Sections list, and `openEvaluation` switches back on.
 */
export function evalIdentitySectionId(templateProductId) {
    if (!templateProductId)
        return null;
    for (const slot of EVAL_PROCESS_SLOTS) {
        const form = EVAL_SLOT_FORMS[slot];
        if (form?.templateProductId === templateProductId)
            return form.identitySectionId;
    }
    return null;
}
/** True for a submission the evaluation process opened (it carries a stage and a slot). */
export function isEvalProcessSubmission(item) {
    return isEvalProcessStage(item?.evaluationStage) && isEvalProcessSlot(item?.evaluationSlot);
}
/**
 * The answers the evaluation process fills in on a slot's submission (D4,
 * extended 2026-10-13): the development-sample stage for the product's current
 * stage, the hardware "Engineer" (the slot's assignee, else the session's
 * user), and the software form's Firmware Version for a version added at
 * Back-and-forth (D33). Each in the `{value, modifiedAt, modifiedBy}` shape DraftEvaluationForm
 * posts, so field history reads as the session user's.
 *
 * Only paths present in the live definition are filled, only when blank, and
 * only with a non-blank value: a value someone entered is never overwritten.
 * Field paths are never renamed.
 */
export function evalSlotPrefill(input) {
    const stamp = { modifiedAt: input.at, modifiedBy: input.userId };
    const engineer = input.engineerName?.trim() || null;
    const candidates = [
        [EVAL_SAMPLE_STAGE_FIELD_PATH, evalSampleStageAnswer(input.stage, input.sampleStageOptions)],
        [EVAL_ENGINEER_FIELD_PATH, engineer],
        // D33: the version added on the board fills the software form's Firmware Version.
        [EVAL_FIRMWARE_VERSION_FIELD_PATH, normaliseEvalSoftwareVersion(input.firmwareVersion)],
    ];
    const data = {};
    for (const [path, value] of candidates) {
        if (value === null || !input.livePaths.has(path))
            continue;
        if (!isBlankEvalAnswer(input.existing?.[path]))
            continue;
        data[path] = { value, ...stamp };
    }
    return data;
}
// ─── Process forms: the identity section as one line (2026-10-14) ─────────
/**
 * Identity sections a process form shows as one read-only line at the top
 * instead of the section: the hardware form's Development Sample. Its Stage
 * and Engineer are filled in by the process (D14), and its two yes/no
 * questions ("Require Supplier Specs for the PLS", "Looked over and confirmed
 * measurements") are covered by the Supplier Spec and PM Spec Review slots
 * (D27, D28). The software form's Firmware Version stays a section: it is
 * the evaluator's own answer at CNC and PLS.
 */
export const EVAL_SUMMARY_IDENTITY_SECTIONS = ['development_sample'];
/**
 * A condition no answers satisfy, an `or` of nothing: by the form's own rules
 * a section under it is not shown and counts toward nothing (no answered, no
 * unanswered, no progress), while its stored answers stay as they are.
 */
export const EVAL_NEVER_CONDITION = { operator: 'or', conditions: [] };
const processFormTemplates = new WeakMap();
/**
 * The template a process form is shown and counted with (2026-10-14): the
 * same template, with each identity section in
 * {@link EVAL_SUMMARY_IDENTITY_SECTIONS} under {@link EVAL_NEVER_CONDITION},
 * so the form shows its one-line summary instead and neither the form nor the
 * stored progress counts its questions. Any other submission (an ordinary
 * ATLAS evaluation) gets `template` itself, unchanged. Fields, paths and
 * answers are untouched; exports and the autofill still see the section.
 * The same view object is returned for the same template, so a host can pass
 * it straight to a provider.
 */
export function evalProcessFormTemplate(template, item) {
    if (!isEvalProcessSubmission(item))
        return template;
    const sections = template.schema?.sections ?? [];
    if (!sections.some((s) => EVAL_SUMMARY_IDENTITY_SECTIONS.includes(s.sectionId)))
        return template;
    const cached = processFormTemplates.get(template);
    if (cached)
        return cached;
    const view = {
        ...template,
        schema: {
            ...template.schema,
            sections: sections.map((s) => EVAL_SUMMARY_IDENTITY_SECTIONS.includes(s.sectionId) ? { ...s, condition: EVAL_NEVER_CONDITION } : s),
        },
    };
    processFormTemplates.set(template, view);
    return view;
}
/** True when a process form shows `sectionId` as its one-line summary instead of the section. */
export function isEvalSummaryIdentitySection(sectionId, item) {
    return isEvalProcessSubmission(item) && EVAL_SUMMARY_IDENTITY_SECTIONS.includes(sectionId);
}
/** An empty stage × slot grid, every cell an empty list. */
export function emptyEvalBoardSlots() {
    return evalStageSlotGrid(() => []);
}
// ─── Points ─────────────────────────────────────────────────────────────────
/** A point that is still live: status open and not soft-deleted. */
export function isEvalPointOpen(point) {
    return point.status === 'open' && !point.deletedAt;
}
/**
 * The states a point may move to from where it is, for the caller: Close and
 * Reopen for anyone who can edit; Future for the PM or an admin only, and
 * never for a Safety point (it has to be Closed). The server applies the
 * same rule; the UI offers only these.
 */
export function evalPointNextStatuses(point, canDecide) {
    const out = [];
    if (point.status !== 'resolved')
        out.push('resolved');
    if (point.status !== 'parked' && canDecide && !point.safety)
        out.push('parked');
    if (point.status !== 'open')
        out.push('open');
    return out;
}
/**
 * What the Fail reconcile does for one (evaluation, question): every Fail in
 * a process form is an Open point (the product lead, 2026-10-13).
 *
 * - A Fail with no point: create one.
 * - A Fail whose point is Closed or Future: reopen it only when the server
 *   closed it (the answer had changed) or the Fail was answered again after
 *   the point was closed or put to Future. A point a person closed ("checked
 *   on PA-000006, fixed") or the PM put to Future stays as they left it while
 *   the same Fail stands.
 * - No longer a Fail (Pass, N/A, cleared, or no longer asked) and the point is
 *   Open: close it, saying why. Closed and Future points are left alone.
 *
 * Never deletes anything.
 */
export function decideFailPoint(answer, point) {
    if (answer.isFail) {
        if (!point)
            return 'create';
        if (point.status === 'open')
            return 'none';
        const change = point.statusChange;
        if (!change)
            return 'none';
        if (change.auto)
            return 'reopen';
        const closedAt = new Date(change.at).getTime();
        return answer.modifiedAt && answer.modifiedAt.getTime() > closedAt ? 'reopen' : 'none';
    }
    return point?.status === 'open' ? 'close' : 'none';
}
/** Every stage × slot of a stored completions map, as readiness reads them. Off-grid keys are left out. */
export function evalSlotCompletionList(map) {
    const out = [];
    for (const stage of EVAL_PROCESS_STAGES) {
        for (const slot of EVAL_PROCESS_SLOTS)
            if (map?.[stage]?.[slot])
                out.push({ stage, slot });
    }
    return out;
}
/**
 * Whether `gate` may be released without an override (D3). Pass every
 * submission, every point and every completion of the product; this function
 * does the filtering.
 *
 * - Only the slots the closing stage lists count (`evalReadinessSlots`):
 *   Photos at CNC and PLS, not at Back-and-forth.
 * - Every form slot needs at least one submitted, locked or published
 *   evaluation, on ANY sample, at the stage the gate closes.
 * - Every upload and points slot needs to have been pressed Completed at that
 *   stage. An evaluation that claims such a slot (one opened before
 *   Photometrics or User Experience stopped being forms) does not complete it.
 * - Gates B and C need every point Closed or Future: any Open, not-deleted
 *   point holds them, whatever stage raised it. Gate A does not look at
 *   points: they carry over to the revised sample and are verified there.
 * - Gate B (D33, D37, D38) judges the software by the LATEST software
 *   version alone (`softwareVersions`): its review done, every point it
 *   lists checked on it or no longer Open. An earlier version's does not
 *   count; with no version it is missing. Full software evaluations are
 *   never required there, a version's Dim Curve is optional (reported,
 *   never required), and User Experience is not a slot there.
 */
export function gateReadiness(gate, submissions, points, completions, softwareVersions = []) {
    const stage = STAGE_BEFORE_GATE[gate];
    const done = new Set();
    for (const sub of submissions) {
        if (sub.item?.evaluationStage !== stage || !EVAL_SLOT_DONE_STATUSES.includes(sub.status))
            continue;
        const slot = sub.item.evaluationSlot;
        if (slot && isEvalProcessSlot(slot) && evalSlotHasForm(slot))
            done.add(slot);
    }
    for (const completion of completions) {
        if (completion.stage === stage && evalSlotIsCompletable(completion.slot))
            done.add(completion.slot);
    }
    let softwareVersion;
    if (stage === EVAL_SOFTWARE_VERSION_STAGE) {
        // Only the latest version counts (D33): its review done (D38).
        const latest = latestEvalSoftwareVersion(softwareVersions);
        softwareVersion = latest
            ? {
                versionKey: latest.versionKey,
                label: latest.label,
                done: latest.reviewed === latest.total,
                reviewed: latest.reviewed,
                total: latest.total,
                dimCurveCompleted: latest.dimCurveCompleted,
            }
            : null;
        done.delete(EVAL_SOFTWARE_VERSION_SLOT);
        if (softwareVersion?.done)
            done.add(EVAL_SOFTWARE_VERSION_SLOT);
    }
    const missingSlots = evalReadinessSlots(stage).filter((slot) => !done.has(slot));
    const open = EVAL_GATES_NEEDING_NO_OPEN_POINTS.includes(gate) ? points.filter((p) => isEvalPointOpen(p)) : [];
    const openPoints = open.map((p) => p.pointId);
    const openSafetyPoints = open.filter((p) => p.safety === true).map((p) => p.pointId);
    return {
        gate,
        ready: missingSlots.length === 0 && openPoints.length === 0,
        missingSlots,
        openPoints,
        openSafetyPoints,
        ...(softwareVersion !== undefined && { softwareVersion }),
    };
}
/**
 * Whether a user may act as the product's PM: release a gate, put a point to
 * Future, untick a point's Safety (D1).
 *
 * - The product's own PM, holding a release role, acts as 'pm'.
 * - Any admin may act; one who is not the product's PM acts as 'admin', which
 *   the server records as an override.
 * - With no active PM, only an admin can act.
 *
 * `pmUserId` is `productWatchService.activePm(productId)?.userId`.
 */
export function resolveEvalPmAuthority(user, pmUserId) {
    const isPm = pmUserId !== null && pmUserId === user.userId;
    if (user.roles.includes('admin'))
        return { allowed: true, actingAs: isPm ? 'pm' : 'admin' };
    if (isPm && canReleaseEvalGate(user.roles))
        return { allowed: true, actingAs: 'pm' };
    if (pmUserId === null)
        return { allowed: false, code: EVAL_PROCESS_ERROR_CODES.gateNoPm };
    return { allowed: false, code: EVAL_PROCESS_ERROR_CODES.gatePmRequired };
}
/**
 * Who may reopen a Completed upload or points slot: whoever completed it, or the
 * product's PM or an admin (the same rule as a gate release, D1).
 */
export function canReopenEvalSlot(user, completion, pmUserId) {
    return completion.completedBy === user.userId || resolveEvalPmAuthority(user, pmUserId).allowed;
}
/**
 * The terms of a release once authority and order are settled (D1, D3).
 *
 * - Ready: released; the note is optional. Stored `override` is true only for
 *   an admin acting outside their own product.
 * - Not ready, no confirmation: refused with EVAL_GATE_NOT_READY (409).
 * - Not ready because of an Open Safety point: refused with
 *   EVAL_GATE_SAFETY_OPEN (409), override or not. A Safety point has to be
 *   Closed (or, by the PM or an admin with a reason, unticked as Safety).
 * - Not ready, confirmed but no note: refused with EVAL_GATE_NOTE_REQUIRED (400).
 * - Not ready, confirmed with a note: released as an override.
 */
export function checkGateRelease(input) {
    const note = input.note?.trim() || null;
    if (input.readiness.ready)
        return { ok: true, override: input.actingAs === 'admin', note };
    if (!input.override)
        return { ok: false, code: EVAL_PROCESS_ERROR_CODES.gateNotReady };
    if (input.readiness.openSafetyPoints.length > 0)
        return { ok: false, code: EVAL_PROCESS_ERROR_CODES.gateSafetyOpen };
    if (!note)
        return { ok: false, code: EVAL_PROCESS_ERROR_CODES.gateNoteRequired };
    return { ok: true, override: true, note };
}
// ─── Failed checks ──────────────────────────────────────────────────────────
/** The answer a pass/fail/na question stores for a fail. */
export const EVAL_FAIL_ANSWER = 'fail';
/** A pass/fail question's notes live in a companion textarea at `<path>-notes`. */
export const EVAL_NOTES_SUFFIX = '-notes';
/** A stored answer is a FieldValue; tolerate a bare value as isFieldAnswered does. */
function answerValue(entry) {
    return entry !== null && typeof entry === 'object' && 'value' in entry
        ? entry.value
        : entry;
}
/**
 * The 'fail' answers in a submission's `data`, each with its companion
 * `<path>-notes` text, in the data's own key order. Pass, na, skip, empty and
 * non-string answers are ignored, and so is a notes textarea that happens to
 * read "fail".
 *
 * `onlyPaths` narrows the scan to known pass/fail fields from the definition,
 * so a free-text answer of "fail" is not mistaken for a failed check.
 */
export function failAnswerPaths(data, onlyPaths) {
    const out = [];
    for (const [fieldPath, entry] of Object.entries(data)) {
        if (fieldPath.endsWith(EVAL_NOTES_SUFFIX))
            continue;
        if (onlyPaths && !onlyPaths.has(fieldPath))
            continue;
        if (answerValue(entry) !== EVAL_FAIL_ANSWER)
            continue;
        const notes = answerValue(data[`${fieldPath}${EVAL_NOTES_SUFFIX}`]);
        out.push({ fieldPath, notes: typeof notes === 'string' && notes.trim() ? notes.trim() : null });
    }
    return out;
}
// ─── Shared evaluations: rounds and section sites (D23 to D25) ──────────────
/** A process evaluation's round: the stored one, or 1 for one opened before rounds (D23). */
export function evalSubmissionRound(item) {
    const round = item?.evaluationRound;
    return typeof round === 'number' && Number.isInteger(round) && round > 0 ? round : 1;
}
/**
 * One open evaluation per product × stage × form slot (D23): the open one is
 * the most recently changed draft (what `openEvaluation` opens); any other
 * draft is an older one, left as it is. With no draft, the slot offers its
 * first round, or a new round once there are evaluations.
 */
export function evalSlotFormState(summaries) {
    const drafts = summaries
        .filter((s) => s.status === 'draft')
        .sort((a, b) => b.lastModifiedAt.localeCompare(a.lastModifiedAt));
    const earlier = summaries
        .filter((s) => s.status !== 'draft')
        .sort((a, b) => b.round - a.round || b.lastModifiedAt.localeCompare(a.lastModifiedAt));
    const latestRound = summaries.reduce((max, s) => Math.max(max, s.round), 0);
    const open = drafts[0] ?? null;
    return {
        open,
        olderDrafts: drafts.slice(1),
        earlier,
        latestRound,
        action: open ? 'open' : summaries.length === 0 ? 'start' : 'new_round',
    };
}
/** The session `userId` had open at `at`: the latest one started before it and not finished before it. */
export function evalSessionAt(sessions, userId, at) {
    let best = null;
    const t = at.getTime();
    for (const s of sessions) {
        if (s.userId !== userId)
            continue;
        const started = new Date(s.startedAt).getTime();
        const finished = s.finishedAt ? new Date(s.finishedAt).getTime() : Infinity;
        if (started <= t && t <= finished && (!best || started > new Date(best.startedAt).getTime()))
            best = s;
    }
    return best;
}
/** `{value, modifiedAt, modifiedBy}` read off a stored answer, or null when it is blank or unstamped. */
function stampedAnswer(entry) {
    if (isBlankEvalAnswer(entry) || entry === null || typeof entry !== 'object')
        return null;
    const { modifiedAt, modifiedBy } = entry;
    if (typeof modifiedBy !== 'string' || !modifiedBy)
        return null;
    const at = modifiedAt instanceof Date ? modifiedAt : typeof modifiedAt === 'string' ? new Date(modifiedAt) : null;
    return at && !Number.isNaN(at.getTime()) ? { at, by: modifiedBy } : null;
}
/**
 * Where each section was worked, as the answers say (D25): the section's
 * earliest answer, its answerer's session at that moment, and that session's
 * unit and site (the site from the session, else `siteOf` the unit). A section
 * with no stamped answer, or answered outside any session, is untagged (null).
 * Never reads anything but the stored answers: nothing hooks autosave.
 */
export function deriveEvalSectionSites(input) {
    const out = new Map();
    for (const section of input.sections) {
        let first = null;
        for (const path of section.fieldPaths) {
            const stamp = stampedAnswer(input.data[path]);
            if (stamp && (!first || stamp.at.getTime() < first.at.getTime()))
                first = stamp;
        }
        const session = first ? evalSessionAt(input.sessions, first.by, first.at) : null;
        out.set(section.sectionId, first && session
            ? {
                site: session.site ?? input.siteOf?.(session.physicalAssetId) ?? null,
                physicalAssetId: session.physicalAssetId,
                by: first.by,
                at: first.at.toISOString(),
                sessionId: session.sessionId,
            }
            : null);
    }
    return out;
}
/**
 * A section's effective site: a person's override, else what the answers say,
 * else untagged. An override names only the site, so the unit is kept only
 * when the answers agree on that site.
 */
export function effectiveEvalSectionSite(derived, override) {
    if (override) {
        return {
            site: override.site,
            physicalAssetId: derived && derived.site === override.site ? derived.physicalAssetId : null,
            source: 'override',
        };
    }
    return derived ? { site: derived.site, physicalAssetId: derived.physicalAssetId, source: 'derived' } : null;
}
/** "EU · PA-000004 · 5 sections, US · PA-000002 · 3 sections": tagged sections by site and unit, most first. */
export function evalSectionContributors(effective) {
    const byKey = new Map();
    for (const tag of effective) {
        if (!tag)
            continue;
        const key = `${tag.site ?? ''}|${tag.physicalAssetId ?? ''}`;
        const known = byKey.get(key) ?? { site: tag.site, physicalAssetId: tag.physicalAssetId, sections: 0 };
        known.sections += 1;
        byKey.set(key, known);
    }
    return [...byKey.values()].sort((a, b) => b.sections - a.sections || (a.site ?? '').localeCompare(b.site ?? '') || (a.physicalAssetId ?? '').localeCompare(b.physicalAssetId ?? ''));
}
//# sourceMappingURL=evaluation-process.compute.js.map